<template>
  <Modal
    :value="value"
    title="邮件后台"
    width="820"
    :footer-hide="true"
    @on-visible-change="$emit('input', $event)"
  >
    <div class="mail-admin-panel">
      <Alert type="info" show-icon>
        当前仅支持 `item / money / exp` 三类附件。白名单角色请在前端和后端控制器中同步维护。
      </Alert>

      <div class="form-row">
        <span class="label">发送范围</span>
        <RadioGroup v-model="sendForm.scope" type="button" size="small">
          <Radio label="single">单人</Radio>
          <Radio label="multi">多人</Radio>
          <Radio label="all">全服</Radio>
        </RadioGroup>
      </div>

      <div class="form-row" v-if="sendForm.scope === 'single'">
        <span class="label">目标角色</span>
        <Input v-model="sendForm.targetCharaId" placeholder="输入单个角色ID" />
      </div>

      <div class="form-row vertical" v-if="sendForm.scope === 'multi'">
        <span class="label">目标角色</span>
        <Input
          v-model="sendForm.targetCharaIdsText"
          type="textarea"
          :rows="4"
          placeholder="输入多个角色ID，支持英文逗号、中文逗号、换行分隔"
        />
      </div>

      <div class="form-row">
        <span class="label">邮件标题</span>
        <Input v-model="sendForm.title" placeholder="请输入邮件标题" />
      </div>

      <div class="form-row">
        <span class="label">发件人</span>
        <Input v-model="sendForm.senderName" placeholder="默认系统" />
      </div>

      <div class="form-row">
        <span class="label">过期时间</span>
        <Input v-model="sendForm.expireTime" placeholder="格式：2026-04-30 23:59:59，可留空" />
      </div>

      <div class="form-row vertical">
        <span class="label">邮件正文</span>
        <Input
          v-model="sendForm.content"
          type="textarea"
          :rows="6"
          placeholder="请输入邮件正文"
        />
      </div>

      <div class="form-row vertical">
        <span class="label">附件 JSON</span>
        <Input
          v-model="sendForm.attachJson"
          type="textarea"
          :rows="8"
          placeholder='例如：[{"kind":"item","itemId":"404095","num":10,"bind":1}]'
        />
      </div>

      <div class="admin-actions">
        <Button @click="resetSendForm">清空</Button>
        <Button type="primary" :loading="sending" @click="submitSendMail">发送邮件</Button>
        <Button type="warning" :loading="runningCompetition" @click="runSectCompetition">手动开赛</Button>
      </div>
    </div>
  </Modal>
</template>

<script>
export default {
  name: 'MailAdminSystem',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      sending: false,
      runningCompetition: false,
      sendForm: {
        scope: 'single',
        targetCharaId: '',
        targetCharaIdsText: '',
        title: '',
        content: '',
        senderName: '系统',
        attachJson: '',
        expireTime: ''
      }
    };
  },
  methods: {
    async runSectCompetition() {
      if (!this.isAdmin) {
        this.$Message.warning('无宗门大比后台权限');
        return;
      }
      this.$Modal.confirm({
        title: '确认手动开赛',
        content: '将按当前已报名弟子立即运行一届全服宗门大比，确认继续吗？',
        onOk: async () => {
          this.runningCompetition = true;
          try {
            const operatorCharaId = this.getCookie('charaId');
            await this.$http.post(`/gameSectCompe/runManually?operatorCharaId=${operatorCharaId}`);
            this.$Message.success('大比已在后台开跑');
          } catch (err) {
            this.$Message.error((err.data && err.data.msg) || '开赛失败');
          } finally {
            this.runningCompetition = false;
          }
        }
      });
    },
    async submitSendMail() {
      if (!this.isAdmin) {
        this.$Message.warning('无发邮件权限');
        return;
      }
      if (!this.sendForm.title || !this.sendForm.content) {
        this.$Message.warning('标题和正文不能为空');
        return;
      }
      this.sending = true;
      try {
        const operatorCharaId = this.getCookie('charaId');
        const payload = {
          mailType: 1,
          title: this.sendForm.title,
          content: this.sendForm.content,
          senderName: this.sendForm.senderName || '系统',
          attachJson: this.sendForm.attachJson || '',
          expireTime: this.sendForm.expireTime || ''
        };

        if (this.sendForm.scope === 'single') {
          if (!this.sendForm.targetCharaId) {
            this.$Message.warning('请填写目标角色ID');
            return;
          }
          await this.$http.post(`/gameMailAdmin/sendMailToPlayer?operatorCharaId=${operatorCharaId}&charaId=${this.sendForm.targetCharaId}`, payload);
        } else if (this.sendForm.scope === 'multi') {
          const charaIds = this.parseCharaIds(this.sendForm.targetCharaIdsText);
          if (!charaIds.length) {
            this.$Message.warning('请填写目标角色列表');
            return;
          }
          await this.$http.post(`/gameMailAdmin/sendMailToPlayers?operatorCharaId=${operatorCharaId}`, {
            ...payload,
            charaIds
          });
        } else {
          await this.$http.post(`/gameMailAdmin/sendMailToAll?operatorCharaId=${operatorCharaId}`, payload);
        }

        this.$Message.success('发送成功');
        this.resetSendForm();
        this.$emit('sent');
      } catch (err) {
        this.$Message.error((err.data && err.data.msg) || '发送失败');
      } finally {
        this.sending = false;
      }
    },
    parseCharaIds(raw) {
      if (!raw) return [];
      return raw.split(/[\n,，\s]+/).map(item => item.trim()).filter(Boolean);
    },
    resetSendForm() {
      this.sendForm = {
        scope: 'single',
        targetCharaId: '',
        targetCharaIdsText: '',
        title: '',
        content: '',
        senderName: '系统',
        attachJson: '',
        expireTime: ''
      };
    }
  }
};
</script>

<style scoped>
.mail-admin-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-row.vertical {
  flex-direction: column;
  align-items: flex-start;
}

.label {
  width: 80px;
  color: #515a6e;
  flex-shrink: 0;
}

.form-row.vertical .label {
  width: auto;
}

.admin-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .label {
    width: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/MailAdminSystem.vue